import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, CalendarRange } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export const OvertimeConfigDialog = ({ open, onOpenChange, onSaved }) => {
  const [mode, setMode] = useState("monthly");
  const [startMonth, setStartMonth] = useState(1);
  const [endMonth, setEndMonth] = useState(12);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.getOvertimeConfig();
        setMode(data.mode || "monthly");
        setStartMonth(data.period_start_month || 1);
        setEndMonth(data.period_end_month || 12);
      } catch {
        /* non bloquant — défauts appliqués */
      }
    })();
  }, [open]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateOvertimeConfig({
        mode,
        period_start_month: startMonth,
        period_end_month: endMonth,
      });
      toast.success("Configuration enregistrée");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const wraps = startMonth > endMonth;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="overtime-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-indigo-600" />
            Calcul des heures supplémentaires
          </DialogTitle>
          <DialogDescription>
            Choisissez comment le compteur d'heures sup / déficit est calculé pour votre société. Ce paramètre s'applique à tous les salariés.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("monthly")}
              className={`rounded-lg border-2 p-3 text-left transition-colors ${mode === "monthly" ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}
              data-testid="mode-monthly"
            >
              <div className="font-medium text-sm">Mensuel</div>
              <div className="text-xs text-muted-foreground mt-1">
                Les heures sup sont comptabilisées chaque mois. La fiche de paie est auto-remplie mensuellement.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode("annual")}
              className={`rounded-lg border-2 p-3 text-left transition-colors ${mode === "annual" ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}
              data-testid="mode-annual"
            >
              <div className="font-medium text-sm">Annuel</div>
              <div className="text-xs text-muted-foreground mt-1">
                Les heures sup sont réconciliées une fois par an, à la fin de la période.
              </div>
            </button>
          </div>

          {mode === "annual" && (
            <div className="rounded-lg border bg-slate-50 p-3 space-y-3">
              <Label className="text-sm font-medium">Période annuelle</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] text-muted-foreground">Début (mois)</Label>
                  <Select value={String(startMonth)} onValueChange={(v) => setStartMonth(parseInt(v, 10))}>
                    <SelectTrigger className="h-9" data-testid="start-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-xs text-muted-foreground mt-5">au</span>
                <div className="flex-1">
                  <Label className="text-[10px] text-muted-foreground">Fin (mois)</Label>
                  <Select value={String(endMonth)} onValueChange={(v) => setEndMonth(parseInt(v, 10))}>
                    <SelectTrigger className="h-9" data-testid="end-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {wraps ? (
                  <>
                    Période à cheval sur deux années civiles : <Badge variant="outline" className="font-mono text-[10px]">{MONTHS[startMonth - 1]} N → {MONTHS[endMonth - 1]} N+1</Badge>
                    <br />
                    Les heures sup du cycle {MONTHS[startMonth - 1]}→{MONTHS[endMonth - 1]} seront auto-remplies sur la fiche de paie de <strong>{MONTHS[endMonth - 1]}</strong>.
                  </>
                ) : (
                  <>
                    Période : <Badge variant="outline" className="font-mono text-[10px]">{MONTHS[startMonth - 1]} → {MONTHS[endMonth - 1]}</Badge>
                    <br />
                    Les heures sup seront auto-remplies sur la fiche de paie de <strong>{MONTHS[endMonth - 1]}</strong>.
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-overtime-config">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OvertimeConfigDialog;
